<!--
  @fileoverview Carousel Component - Horizontal Card Slider (Vue 3)

  Displays message content as horizontally swipeable cards using Swiper.js.
  Each card can contain an image, title, subtitle, and action buttons.

  @component MessageCarousel
  @displayName Card Carousel

  Features:
  - Touch/swipe navigation on mobile
  - Arrow navigation on desktop
  - Dot pagination indicators (UI Beta mode)
  - Image lightbox on click
  - Centered slide with partial neighbor visibility
  - Read more truncation for long subtexts
  - RTL layout support
  - Mobile Safari overflow fix

  @example
  <MessageCarousel
    :message="carouselMessage"
    :button-outline="'primary'"
    :should-disable-if-in-livechat="false"
    @sendPostback="handleButtonClick"
    @setLightBoxImage="openLightbox"
  />

  @vue3migration
  - Migrated to script setup syntax
  - Replaced mapGetters with useMainStore()
  - Replaced $set with direct reactive assignment
  - Updated slot syntax from slot="name" to #name
  - Replaced $refs with useTemplateRef
-->
<template>
  <!-- Swiper carousel container -->
  <Swiper
    ref="krCarouselRef"
    class="my-[10px] pb-[10px]"
    :class="{
      'overflow-auto': isMobileSafari,
      'overflow-hidden': !isMobileSafari,
    }"
    slides-per-view="auto"
    :centered-slides="true"
    :space-between="20"
    direction="horizontal"
    :allow-touch-move="allowTouchMove"
    @swiper="onSwiperInit"
    @slideChange="handleSlideChange"
  >
    <!-- slides -->
    <SwiperSlide
      v-for="(item, index) in message.data.content"
      :key="index"
      class="max-w-[65%] h-auto"
    >
      <el-card
        class="message-card-item h-fit"
        :class="{ 'message-card-item--has-image-text': itemImage(item) && item.text }"
      >
        <div v-if="itemImage(item) && item.text" class="bg-white">
          <img
            v-if="itemImage(item)"
            :src="itemImage(item)"
            :class="[
              'h-[160px] w-full',
              !settings.disableLightBoxImage ? 'cursor-pointer' : '',
              imageObjectClass(item),
            ]"
            @click="setLightBoxImage(item)"
          />
        </div>

        <div v-else-if="itemImage(item)">
          <a
            :class="!settings.disableLightBoxImage ? 'cursor-pointer' : ''"
            @click="setLightBoxImage(item)"
          >
            <img class="w-full" :src="itemImage(item)" />
          </a>
        </div>

        <div class="message-card-content flex !flex-col h-full">
          <div v-if="item.text || item.subtext" class="p-2">
            <div class="leading-[18px]">
              <span
                v-text="item.text"
                class="message-card-title carousel-font !font-semibold !text-base"
              />
            </div>
            <div class="my-1 leading-4">
              <div
                v-if="
                  !shouldShowFullContent[index] &&
                  readMoreEnabled &&
                  item.subtext?.length > readMoreCharacterCount
                "
              >
                <small class="subtext carousel-font !text-sm !text-[#333]">
                  {{ truncateText(item.subtext) }}
                </small>
                <div class="keyreply-read-more" @click="setShowFullContent(index)">Read More</div>
              </div>
              <div v-else>
                <small class="subtext carousel-font text-sm text-[#333]">
                  {{ item.subtext }}
                </small>
              </div>
            </div>
          </div>

          <el-row type="text" class="!flex-col !px-[10px]">
            <div v-for="(button, buttonIndex) in item.buttons" :key="`button-${buttonIndex}`">
              <el-button
                v-if="
                  !((button.type && button.type === 'website') || button.url) ||
                  isDisabledButton(button) ||
                  button.popupWindow?.enabled
                "
                class="message-card-button carousel-font !w-full !mb-[10px] !whitespace-normal"
                :class="{ 'blue-button': buttonOutline === 'primary' }"
                v-text="button.text"
                target="_blank"
                :plain="true"
                :type="buttonOutline"
                :disabled="isDisabledButton(button)"
                @click.stop.prevent="handleButtonClick($event, button)"
              />
              <a
                v-else
                @click="emit('sendPostback', button)"
                :href="validUrl(button.url)"
                :target="button.popup ?? '_blank'"
              >
                <el-button
                  class="message-card-button carousel-font w-full mb-[10px] whitespace-normal"
                  :class="{ 'blue-button': buttonOutline === 'primary' }"
                  v-text="button.text"
                  target="_blank"
                  :plain="true"
                  :type="buttonOutline"
                />
              </a>
            </div>
          </el-row>
        </div>
        <div
          class="navigation carousel-font"
          v-if="totalSlides > 1 && isUiBetaEnabled"
          :class="{ disabled: current !== index }"
        >
          <button v-if="current > 0" class="navigation-prev" @click="prev">
            <el-icon>
              <ArrowRight v-if="isRtlModeEnabled" />
              <ArrowLeft v-else />
            </el-icon>
          </button>
          <button class="navigation-next" @click="next" v-if="current < totalSlides - 1">
            <el-icon>
              <ArrowLeft v-if="isRtlModeEnabled" />
              <ArrowRight v-else />
            </el-icon>
          </button>
        </div>
      </el-card>
    </SwiperSlide>

    <!-- Navigation buttons using Vue 3 slot syntax -->
    <template #button-prev>
      <button
        v-if="message.data.content.length > 1 && !isUiBetaEnabled"
        class="el-carousel__arrow el-carousel__arrow--left carousel-font carousel-arrow"
        @click="prev"
      >
        <el-icon><ArrowLeft /></el-icon>
      </button>
    </template>
    <template #button-next>
      <button
        v-if="message.data.content.length > 1 && !isUiBetaEnabled"
        class="el-carousel__arrow el-carousel__arrow--right carousel-font carousel-arrow"
        @click="next"
      >
        <el-icon><ArrowRight /></el-icon>
      </button>
    </template>

    <!-- Pagination dots using Vue 3 slot syntax -->
    <template #pagination>
      <div class="pagination" v-if="totalSlides > 1 && isUiBetaEnabled">
        <span
          class="dot"
          v-for="(item, index) in message.data.content"
          :key="index"
          :class="{ active: current === index }"
          @click="handleNavigateSlide(index)"
        />
      </div>
    </template>
  </swiper>
</template>

<script setup>
/**
 * @module components/ChatWindow/Message/Carousel
 * @description Horizontal swipeable card carousel using Swiper.js (Vue 3 Composition API)
 */
import { ref, computed, reactive } from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import * as _ from "lodash-es";
import { ArrowLeft, ArrowRight } from "@element-plus/icons-vue";
import isDisabledButtonHelper from "@/helpers/isDisabledButton";
import { useMainStore } from "@/stores/main";
import validUrl from "@/helpers/validUrl";

/**
 * Component props with proper types
 */
const props = defineProps({
  /** Message object with content array */
  message: {
    type: Object,
    required: true,
  },
  /** Element UI button type */
  buttonOutline: {
    type: String,
    default: "",
  },
  /** Disable buttons during live chat */
  shouldDisableIfInLivechat: {
    type: Boolean,
    default: false,
  },
  /** Custom background CSS (unused?) */
  backgroundStyle: {
    type: String,
    default: "",
  },
  /** Custom title CSS (unused?) */
  titleStyle: {
    type: String,
    default: "",
  },
  /** Custom icon CSS (unused?) */
  iconStyle: {
    type: String,
    default: "",
  },
});

/**
 * Component emits
 */
const emit = defineEmits(["sendPostback", "setLightBoxImage"]);

/**
 * Pinia store instance
 */
const store = useMainStore();

/**
 * Swiper instance reference (Swiper 11 stores instance via @swiper event)
 */
const swiperInstance = ref(null);

// ============================================
// Reactive State
// ============================================

/** Currently active slide index */
const current = ref(0);

/** Map of slide indices to "show full content" state - using reactive for direct assignment */
const shouldShowFullContent = reactive({});

// ============================================
// Computed Properties
// ============================================

/**
 * Check if RTL mode is enabled
 * @returns {boolean} True if RTL mode is enabled
 */
const isRtlModeEnabled = computed(() => store.isRtlModeEnabled);

/**
 * Check if UI Beta features are enabled
 * @returns {boolean} True if UI Beta is enabled
 */
const isUiBetaEnabled = computed(() => store.isUiBetaEnabled);

/**
 * Access webchat settings from Pinia store
 * @returns {Object} Settings configuration
 */
const settings = computed(() => store.settings);

/**
 * Detect Mobile Safari browser
 *
 * Mobile Safari has specific overflow handling issues that
 * require different CSS treatment.
 *
 * @returns {boolean} True if running in Mobile Safari
 */
const isMobileSafari = computed(() => {
  let ua = window.navigator.userAgent;
  return /(iPad|iPhone|iPod).*WebKit/.test(ua) && !/(CriOS|OPiOS)/.test(ua);
});

/**
 * Get Swiper instance
 * @returns {Object} Swiper.js instance
 */
const swiper = computed(() => {
  return swiperInstance.value;
});

/**
 * Total number of slides in carousel
 * @returns {number} Slide count
 */
const totalSlides = computed(() => {
  return props.message.data.content.length;
});

/**
 * Whether touch/swipe navigation is enabled
 * Disabled for single-item carousels
 * @returns {boolean} True if swipe is allowed
 */
const allowTouchMove = computed(() => {
  return totalSlides.value > 1;
});

/**
 * Character limit for "Read more" truncation
 * @returns {number} Character count threshold
 */
const readMoreCharacterCount = computed(() => {
  return settings.value.readMoreConfigure?.readMoreCharacterCount || 150;
});

/**
 * Whether "Read more" feature is enabled
 * @returns {boolean} True if enabled in settings
 */
const readMoreEnabled = computed(() => {
  return settings.value.readMoreConfigure?.enabled || false;
});

// ============================================
// Methods
// ============================================

/**
 * Initialize swiper instance reference
 * Called by @swiper event in Swiper 11
 *
 * @param {Object} swiperRef - Swiper.js instance
 */
function onSwiperInit(swiperRef) {
  swiperInstance.value = swiperRef;
}

/**
 * Open image in lightbox viewer
 *
 * @param {Object} item - Content item with image
 * @emits setLightBoxImage - Image URL for lightbox
 */
function setLightBoxImage(item) {
  if (settings.value.disableLightBoxImage) {
    return;
  }
  emit("setLightBoxImage", itemImage(item));
}

/**
 * Extract image URL from content item
 *
 * Checks both single image and images array formats.
 *
 * @param {Object} item - Content item
 * @returns {string|undefined} Image URL
 */
function itemImage(item) {
  const image = item.image || _.get(item, "images[0]");
  return image;
}

/**
 * Navigate to next slide
 */
function next() {
  swiper.value?.slideNext();
}

/**
 * Navigate to previous slide
 */
function prev() {
  swiper.value?.slidePrev();
}

/**
 * Check if button should be disabled
 * @param {Object} button - Button configuration
 * @returns {boolean} True if disabled
 */
function isDisabledButton(button) {
  // Create a context object that mimics the component instance
  // for backward compatibility with the helper function
  const context = {
    message: props.message,
    settings: settings.value,
    shouldDisableIfInLivechat: props.shouldDisableIfInLivechat,
    $store: {
      getters: {
        settings: settings.value,
        messages: store.messages,
        isSendingPostback: store.isSendingPostback,
      },
    },
  }
  return isDisabledButtonHelper(context, button)
}

/**
 * Handle button click within carousel card
 *
 * Supports popup windows with custom dimensions and
 * emits postback for all button types.
 *
 * @param {Event} $event - DOM click event
 * @param {Object} button - Button configuration
 * @emits sendPostback - Button data for processing
 */
function handleButtonClick($event, button) {
  // Handle popup window with custom dimensions
  if (button.url && button.popupWindow?.enabled) {
    $event.preventDefault();
    const width = button.popupWindow.width || 500;
    const height = button.popupWindow.height || 600;
    const windowFeatures = `width=${width},height=${height}`;
    window.open(validUrl(button.url), "_blank", windowFeatures);
  }
  emit("sendPostback", button);
}

/**
 * Navigate to specific slide by index (pagination click)
 * @param {number} index - Target slide index
 */
function handleNavigateSlide(index) {
  swiper.value?.slideTo(index);
}

/**
 * Handle Swiper slide change event
 * Updates current index for pagination indicators
 */
function handleSlideChange() {
  current.value = swiper.value?.activeIndex || 0;
}

/**
 * Truncate text with ellipsis for "Read more" preview
 * @param {string} text - Full text content
 * @returns {string} Truncated text with ellipsis
 */
function truncateText(text) {
  if (!text) return "";
  return text.slice(0, readMoreCharacterCount.value) + "...";
}

/**
 * Mark slide content as expanded (show full text)
 *
 * In Vue 3, direct assignment to reactive objects works
 * without needing Vue.set() or $set().
 *
 * @param {number} index - Slide index to expand
 */
function setShowFullContent(index) {
  shouldShowFullContent[index] = true;
}

/**
 * Get CSS object-fit class for image
 *
 * @param {Object} item - Content item with options
 * @returns {string} CSS class (object-cover or object-contain)
 */
function imageObjectClass(item) {
  return !_.get(item.options, "padded") ? "object-cover" : "object-contain";
}
</script>

<style lang="scss" scoped>
:deep(.swiper-wrapper) {
  height: auto;
  align-items: stretch;
}
</style>

<style lang="scss" scoped>
.carousel-font {
  font-family: var(--keyreply-font-family);
}

.carousel-arrow {
  background: rgba(31, 45, 61, 0.5) !important;
  min-width: auto !important;
}

.keyreply-read-more {
  color: var(--keyreply-agent-text-color);
  font-weight: 600;
  font-style: italic;
  cursor: pointer;
}

.message-card-item {
  background: var(--keyreply-user-main-bg-color);
}

.message-card-item :deep(.el-card__body) {
  padding: 0;
  height: fit-content;
  display: block;
  flex-direction: column;
}

.message-card-item--has-image-text :deep(.el-card__body) {
  display: flex;
}

.ui-beta .message-card-title {
  color: var(--keyreply-user-main-title-color);
}

.ui-beta {
  .swiper-container {
    overflow: visible !important;
  }

  .swiper-slide {
    max-width: 75% !important;
  }

  .message-card-item {
    border-radius: 16px;
    overflow: hidden;
    // height: 100% !important;
  }

  .message-card-content {
    text-align: left;
  }

  .message-card-button {
    display: inline-block;
    border-radius: 8px;
    font-size: 12px;

    &.blue-button {
      background: #fff;
      border: 1px solid #4e6cce;
      color: #4e6cce;

      &:hover,
      &:focus,
      &:active {
        background: #4e6cce !important;
        color: #fff;
      }
    }
  }

  .message-card-title {
    display: inline-block;
    font-size: 20px;
    margin-bottom: 16px;
    text-align: left;
  }

  .pagination {
    display: flex;
    justify-content: center;
    margin-top: 16px;
    width: 100%;

    .dot {
      width: 4px;
      height: 4px;
      background: #999;
      border-radius: 200px;
      cursor: pointer;
      transition: all 0.5s ease-in-out;

      &.active {
        background: #333;
      }
    }
  }

  .navigation {
    display: flex;
    justify-content: space-between;
    box-sizing: border-box;
    padding: 8px;

    &.disabled {
      opacity: 0;
    }

    &-next,
    &-prev {
      -webkit-text-size-adjust: none !important;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 27px !important;
      height: 27px !important;
      min-width: 27px !important;
      min-height: 27px !important;
      border-radius: 50%;
      border: none;
      outline: none;
      cursor: pointer;
      background: var(--keyreply-widget-icon-bg-color);
      color: var(--keyreply-widget-icon-text-color);

      i {
        font-size: 16px;
      }
    }
    &-next {
      i {
        margin-left: 2px;
      }
    }
  }
}

[dir="ltr"] .message-card-content {
  text-align: left;
  padding: 10px;
}

[dir="rtl"] .message-card-content {
  text-align: right;
}

[dir="ltr"] .message-card-button {
  margin-right: 8px !important;
}

[dir="rtl"] .message-card-button {
  margin-left: 8px !important;
}

[dir="ltr"] .ui-beta .pagination {
  .dot {
    margin-right: 3px;
  }
}

[dir="rtl"] .ui-beta .pagination {
  .dot {
    margin-left: 3px;
  }
}

[dir="ltr"] .ui-beta .navigation {
  &-next {
    margin-left: auto;
  }
}

[dir="rtl"] .ui-beta .navigation {
  &-next {
    margin-right: auto;
  }
}
</style>

<style lang="scss">
.ui-beta {
  .message-card-item .el-card__body {
    height: 100% !important;
    display: flex !important;
    justify-content: space-between !important;
  }
  .swiper-wrapper {
    height: auto !important;
  }
}
</style>
